<template>
    <div class="pie-chart-container">
        <div ref="chartContainer" class="chart-container"></div>
    </div>
</template>

<script>
import * as echarts from 'echarts';
import { debounce } from 'lodash';
import _ from 'lodash';

export default {
    name: 'PieChart',
    props: {
        data: {
            type: Array,
            required: true
        },
        settings: {
            type: Object,
            required: true
        },
        title: {
            type: String,
            required: true
        },
        fields: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            chart: null,
            resizeObserver: null
        };
    },
    computed: {
        xAxisField() {
            return this.fields.find(f => f._id === this.settings.xAxis.field);
        },
        yAxisField() {
            if (this.settings.yAxis.calculationType === 'count') return null;
            return this.fields.find(f => f._id === this.settings.yAxis.field);
        },
        $_() {
            return _;
        }
    },
    watch: {
        data: {
            handler() {
                this.updateChart();
            },
            deep: true
        },
        settings: {
            handler() {
                this.updateChart();
            },
            deep: true
        }
    },
    methods: {
        initChart() {
            if (this.chart) {
                this.chart.dispose();
            }

            // Ensure container exists and has dimensions
            if (!this.$refs.chartContainer || 
                !this.$refs.chartContainer.offsetWidth || 
                !this.$refs.chartContainer.offsetHeight) {
                return;
            }

            this.chart = echarts.init(this.$refs.chartContainer);
            this.updateChart();
            this.initResizeObserver();
        },
        initResizeObserver() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
            }

            this.resizeObserver = new ResizeObserver(_.debounce(() => {
                if (this.chart && this.$refs.chartContainer) {
                    requestAnimationFrame(() => {
                        this.chart.resize();
                    });
                }
            }, 100));

            if (this.$refs.chartContainer) {
                this.resizeObserver.observe(this.$refs.chartContainer);
            }
        },
        updateChart() {
            if (!this.chart || !this.data || !this.data.length) return;

            const xAxisField = this.fields.find(f => f._id === this.settings.xAxis.field);
            if (!xAxisField) return;

            // Prepare data for pie chart
            const pieData = this.data.map(item => {
                const name = item[xAxisField.name];
                const value = item.value;
                const customLabel = this.$_.get(this.settings, `customization.labelCustomization.${name}`);

                return {
                    name: customLabel?.label || name,
                    value: value,
                    itemStyle: {
                        color: customLabel?.color
                    },
                    label: {
                        show: true,
                        formatter: params => {
                            const percent = ((params.value / this.data.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1);
                            return `${params.name}\n${params.value} (${percent}%)`;
                        }
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: '15',
                            fontWeight: 'bold'
                        }
                    }
                };
            });

            // Sort data if needed
            if (this.settings.customization?.sortBy) {
                const [axis, direction] = this.settings.customization.sortBy.split('-');
                const sortOrder = direction === 'asc' ? 1 : -1;
                pieData.sort((a, b) => {
                    if (axis === 'x') {
                        return sortOrder * (a.name > b.name ? 1 : -1);
                    } else {
                        return sortOrder * (a.value - b.value);
                    }
                });
            }

            // Apply top/bottom filter if enabled
            let filteredData = [...pieData];
            if (this.settings.customization?.showOnlyEnabled && 
                this.settings.customization?.showOnly?.mode && 
                this.settings.customization?.showOnly?.take) {
                const { mode, take } = this.settings.customization.showOnly;
                filteredData.sort((a, b) => b.value - a.value);
                if (mode === 'top') {
                    filteredData = filteredData.slice(0, take);
                } else if (mode === 'bottom') {
                    filteredData = filteredData.slice(-take);
                }
            }

            // Remove empty values if configured
            if (this.settings.customization?.showEmptyValues === false) {
                filteredData = filteredData.filter(item => item.value !== null && item.value !== undefined && !isNaN(item.value));
            }

            const option = {
                title: {
                    text: this.title,
                    left: 'center'
                },
                tooltip: {
                    trigger: 'item',
                    formatter: params => {
                        const percent = ((params.value / filteredData.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1);
                        return `${params.name}<br/>${params.value} (${percent}%)`;
                    }
                },
                legend: {
                    type: 'scroll',
                    orient: 'vertical',
                    right: 10,
                    top: 'center',
                    data: filteredData.map(item => item.name)
                },
                series: [
                    {
                        name: this.yAxisField?.name || 'Değer',
                        type: 'pie',
                        radius: '55%',
                        center: ['40%', '50%'],
                        data: filteredData,
                        emphasis: {
                            itemStyle: {
                                shadowBlur: 10,
                                shadowOffsetX: 0,
                                shadowColor: 'rgba(0, 0, 0, 0.5)'
                            }
                        }
                    }
                ]
            };

            this.chart.setOption(option, true);
        }
    },
    mounted() {
        this.$nextTick(() => {
            requestAnimationFrame(() => {
                this.initChart();
            });
        });
    },
    beforeDestroy() {
        if (this.chart) {
            this.chart.dispose();
            this.chart = null;
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }
};
</script>

<style lang="scss" scoped>
.pie-chart-container {
    width: 100%;
    height: 100%;
    min-height: 200px;
    position: relative;

    .chart-container {
        width: 100%;
        height: 100%;
        min-height: 200px;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
    }
}
</style> 